/*
 * SereneGuard member benefits activation.
 *
 * The submit goes out as a real form navigation, because Shopify's storefront
 * spam protection owns the submit event on `/contact` forms. Its bootstrap does:
 *
 *   document.addEventListener('submit', event => {
 *     const notBound     = protectable(form) && !form.dataset.hcaptchaBound;
 *     const needsPersist = protected(form) && !form.elements['form_key']?.value;
 *     (notBound || needsPersist) && event.preventDefault();
 *     needsPersist && !notBound && (persist(form), form.submit());
 *   });
 *
 * When the form is NOT bound yet, Shopify calls preventDefault() and then does
 * nothing at all — no token, no navigation. Binding only starts on the first
 * focusin/change inside the form and needs hcaptcha.com to load. An earlier
 * build disabled the button on submit with no recovery of any kind, so a click
 * that landed before binding — or any click at all where hcaptcha.com is slow or
 * blocked — left the customer on a dead "Activating…" button for good.
 *
 * So this file:
 *   1. primes Shopify's binding early, using Shopify's own focusin trigger;
 *   2. if a click still arrives unbound, takes the event, waits for binding, and
 *      hands it back; if binding never completes it keeps the customer here with
 *      a retryable message, because Shopify rejects any post without a token;
 *   3. never disables the button without a watchdog that gives it back if the
 *      page has not actually started unloading — one that treats an open
 *      challenge overlay as a legitimate wait rather than a swallowed submit.
 *
 * Every branch of a valid submit therefore ends in a navigation or a visible,
 * retryable message. Nothing can silently do nothing.
 *
 * Success is rendered server-side by `form.posted_successfully?`, so there is no
 * client-side state to carry across the post.
 */
(() => {
  const BIND_TIMEOUT = 12000;
  const LEAVE_TIMEOUT = 9000;
  const CHALLENGE_POLL = 400;
  const CAPTCHA_ERROR =
    "Secure verification couldn’t load. Please check your connection or content blocker, then tap the button again.";
  const STUCK_ERROR = "That didn’t go through. Please tap the button again.";

  /* ------------------------------------------------------------------ captcha */

  function captchaActive() {
    return !!(window.Shopify && window.Shopify.captcha) || !!document.getElementById("captcha-bootstrap");
  }

  function captchaBound(form) {
    return Boolean(form.dataset.hcaptchaBound || form.dataset.recaptchaBound);
  }

  /* Shopify binds its captcha on the first focusin/change inside a protected
     form. Fire that trigger ourselves so binding is under way long before the
     customer reaches the button. */
  function primeCaptcha(form) {
    if (!captchaActive() || captchaBound(form)) return;
    const now = Date.now();
    if (now - Number(form.dataset.sgPrimedAt || 0) < 1000) return;
    form.dataset.sgPrimedAt = String(now);
    try { form.dispatchEvent(new Event("focusin", { bubbles: true })); } catch (_) { /* ignore */ }
  }

  function whenCaptchaBound(form, timeout) {
    return new Promise((resolve) => {
      if (captchaBound(form)) { resolve(true); return; }
      const started = Date.now();
      const timer = setInterval(() => {
        if (captchaBound(form)) { clearInterval(timer); resolve(true); }
        else if (Date.now() - started >= timeout) { clearInterval(timer); resolve(false); }
      }, 120);
    });
  }

  /* A visible challenge is the one legitimate reason for a submitted page to sit
     still: the customer is solving a puzzle in an iframe overlay, which routinely
     outlasts LEAVE_TIMEOUT and can chain several rounds. The navigation watchdog
     has to tell that apart from a submit that was swallowed. */
  function shown(el) {
    if (typeof el.checkVisibility === "function") {
      return el.checkVisibility({ visibilityProperty: true, opacityProperty: true });
    }
    let node = el;
    while (node && node.nodeType === 1) {
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
      node = node.parentElement;
    }
    return true;
  }

  function challengeOpen() {
    const frames = document.querySelectorAll('iframe[src*="hcaptcha.com"], iframe[src*="recaptcha"]');
    for (let i = 0; i < frames.length; i += 1) {
      const rect = frames[i].getBoundingClientRect();
      /* The badge and the pre-loaded checkbox frame are small; the overlay is not. */
      if (rect.width < 200 || rect.height < 200) continue;
      if (shown(frames[i])) return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------- cloud copy
     A best-effort copy of an accepted submission, sent to the collector that
     feeds the Tencent sheet. It never intercepts Shopify's CAPTCHA, never
     resubmits the form, and never blocks navigation: the Shopify email remains
     the system of record, and this is a parallel copy. Failures are retried from
     an outbox that survives the post's return navigation. */
  function createJourneyId() {
    return `sgm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function createCloudCollector(brand) {
    const endpoint = "https://us-central1-sereneflow-78ba3.cloudfunctions.net/collectSereneGuardMember";
    const key = `member-cloud-outbox-${brand}`;
    const ttl = 60 * 60 * 1000;
    const pending = new Map();
    const sending = new Set();
    let retryTimer = 0;

    function readStored() {
      for (const storage of ["sessionStorage", "localStorage"]) {
        try {
          const rows = JSON.parse(window[storage].getItem(key) || "[]");
          if (Array.isArray(rows)) for (const item of rows) {
            if (item && typeof item.id === "string" && item.at > Date.now() - ttl && item.body?.brand === brand) pending.set(item.id, item);
          }
        } catch (_) { /* Keepalive delivery also works when storage is blocked. */ }
      }
    }

    function save() {
      const now = Date.now();
      for (const [id, item] of pending) if (now - item.at > ttl) pending.delete(id);
      const rows = JSON.stringify([...pending.values()]);
      for (const storage of ["sessionStorage", "localStorage"]) {
        try {
          if (pending.size) window[storage].setItem(key, rows);
          else window[storage].removeItem(key);
        } catch (_) { /* The network copy is still attempted. */ }
      }
    }

    async function deliver(item) {
      if (sending.has(item.id)) return;
      sending.add(item.id);
      try {
        const response = await fetch(endpoint, {
          method: "POST", credentials: "omit", redirect: "error", keepalive: true,
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
          body: JSON.stringify(item.body)
        });
        const result = await response.json();
        if (response.ok && result.ok === true && pending.get(item.id) === item) {
          pending.delete(item.id);
          save();
        }
      } catch (_) { /* Retain the outbox through Shopify's return navigation. */ }
      finally { sending.delete(item.id); }
      if (pending.size && !retryTimer) retryTimer = window.setTimeout(() => { retryTimer = 0; flush(); }, 30000);
    }

    function flush() { save(); for (const item of pending.values()) deliver(item); }

    /* The collector's field names and allowed values are a contract with the
       validator in integrations/sereneguard-sync/functions/records.js. Changing a
       select option here without changing it there silently drops the row. */
    function collect(form, journeyId) {
      try {
        const data = new FormData(form);
        const names = ["Purchase channel", "Order number", "name", "email", "Style", "Purchase date", "Privacy consent"];
        const fields = {};
        for (const name of names) fields[name] = String(data.get(`contact[${name}]`) || "");
        const id = `${journeyId}:activation`;
        const item = { id, at: Date.now(), body: { brand, journeyId, stage: "activation", fields } };
        pending.set(id, item);
        save();
        deliver(item);
      } catch (_) { /* The original Shopify submission always remains available. */ }
    }

    readStored();
    if (pending.size) flush();
    window.addEventListener("online", flush);
    window.addEventListener("pageshow", flush);
    return { collect };
  }

  /* Page-view beacon. Fire-and-forget: it must never block or break the page.
     Paste into each storefront's own JS and pass that storefront's brand key.
     text/plain avoids a CORS preflight, so one view costs one request. */
  var pageViewSent = false;
  function sendPageView(brand, preview) {
    if (preview || pageViewSent) return;
    pageViewSent = true;
    try {
      fetch("https://us-central1-sereneflow-78ba3.cloudfunctions.net/collectPageView", {
        method: "POST", credentials: "omit", redirect: "error", keepalive: true,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: JSON.stringify({ brand: brand })
      }).catch(function () { /* Analytics must never surface an error to a customer. */ });
    } catch (_) { /* Blocked fetch, offline, or a hardened browser: count nothing. */ }
  }

  /* --------------------------------------------------------------------- page */

  const roots = document.querySelectorAll("[data-sg-warranty]");

  roots.forEach((root) => {
    const form = root.querySelector("form");
    const purchaseDate = root.querySelector("[data-purchase-date]");
    const submit = root.querySelector("[data-submit]");
    const channel = root.querySelector("[data-purchase-channel]");
    const amazonOrderField = root.querySelector("[data-amazon-order-field]");
    const amazonOrder = root.querySelector("[data-amazon-order]");
    const note = root.querySelector("[data-form-error]");

    if (purchaseDate) {
      purchaseDate.max = new Date().toISOString().slice(0, 10);
    }

    const syncChannel = () => {
      if (!channel || !amazonOrderField || !amazonOrder) return;
      const isAmazon = channel.value === "Amazon";
      amazonOrderField.hidden = !isAmazon;
      amazonOrder.disabled = !isAmazon;
      amazonOrder.required = isAmazon;
      if (!isAmazon) amazonOrder.value = "";
    };

    channel?.addEventListener("change", syncChannel);
    syncChannel();

    if (!form || !submit) return;

    /* The consent line doubles as the form's message slot. Its Liquid text is the
       consent case; anything else is set here and reset on the next consent miss. */
    const consentMessage = note ? note.textContent : "";

    function showNote(message) {
      if (!note) return;
      note.textContent = message;
      note.hidden = false;
    }

    function hideNote() {
      if (note) note.hidden = true;
    }

    /* One id per page load, not per click: a retry after a failed bind must land
       on the same row rather than creating a second one. */
    const journeyId = createJourneyId();
    const cloudCollector = createCloudCollector("sereneguard");
    sendPageView("sereneguard", false);

    const label = submit.querySelector("span");

    function setBusy(busy, busyLabel) {
      form.dataset.busy = busy ? "true" : "false";
      if (label) {
        if (busy) {
          if (!submit.dataset.idleLabel) submit.dataset.idleLabel = label.textContent;
          label.textContent = busyLabel || submit.dataset.loadingLabel || "Activating…";
        } else if (submit.dataset.idleLabel) {
          label.textContent = submit.dataset.idleLabel;
        }
      }
      submit.disabled = busy;
      submit.classList.toggle("is-loading", busy);
      submit.setAttribute("aria-busy", String(busy));
    }

    let watchdog = 0;
    let leaving = false;

    /* The button is only ever disabled together with this timer. If the page has
       not begun unloading by the time it fires, the submit was swallowed
       somewhere out of our reach — give the customer their button back.

       An open challenge defers the deadline instead of cancelling the timer:
       solving one takes far longer than LEAVE_TIMEOUT. Closing the challenge
       without solving it still gives the button back, one LEAVE_TIMEOUT later. */
    function expectNavigation() {
      leaving = true;
      setBusy(true);
      clearInterval(watchdog);

      let deadline = Date.now() + LEAVE_TIMEOUT;
      let challenged = false;

      watchdog = setInterval(() => {
        if (!leaving) { clearInterval(watchdog); return; }

        if (challengeOpen()) {
          if (!challenged) {
            challenged = true;
            setBusy(true, "Waiting for verification…");
          }
          deadline = Date.now() + LEAVE_TIMEOUT;
          return;
        }
        if (Date.now() < deadline) return;

        clearInterval(watchdog);
        leaving = false;
        setBusy(false);
        showNote(STUCK_ERROR);
      }, CHALLENGE_POLL);
    }

    window.addEventListener("pagehide", () => { clearInterval(watchdog); });

    form.addEventListener("submit", (event) => {
      /* Our own re-submit after binding: hand it straight to Shopify. */
      if (form.dataset.sgRetry === "true") {
        form.dataset.sgRetry = "false";
        return;
      }
      if (leaving || form.dataset.busy === "true") {
        event.preventDefault();
        return;
      }

      const privacy = form.querySelector("[data-privacy-consent]");
      if (privacy && !privacy.checked) {
        event.preventDefault();
        privacy.focus();
        showNote(consentMessage);
        return;
      }

      if (!form.checkValidity()) return;
      hideNote();
      cloudCollector.collect(form, journeyId);

      /* Nothing will interfere: let the browser navigate, or let Shopify's bound
         handler persist the form and submit it itself. */
      if (!captchaActive() || captchaBound(form)) {
        expectNavigation();
        return;
      }

      /* Unbound. Shopify is about to swallow this event and do nothing, so take
         it, get the binding going, and hand the submit back once it is ready. If
         hcaptcha.com never answers, keep the customer on this page with a
         retryable message; Shopify rejects any post without a token. */
      event.preventDefault();
      setBusy(true);
      primeCaptcha(form);

      whenCaptchaBound(form, BIND_TIMEOUT).then((bound) => {
        if (!bound) {
          setBusy(false);
          showNote(CAPTCHA_ERROR);
          return;
        }
        /* The customer can still edit fields while binding is in flight. */
        if (!form.checkValidity()) {
          setBusy(false);
          return;
        }
        if (typeof form.requestSubmit !== "function") {
          setBusy(false);
          showNote(CAPTCHA_ERROR);
          return;
        }
        expectNavigation();
        form.dataset.sgRetry = "true";
        form.requestSubmit();
      });
    });

    /* Step 1 is the only thing that posts — start its binding now, so a click is
       never what starts it. */
    setTimeout(() => primeCaptcha(form), 400);
  });
})();
